/**
 * Copyright (c) 2024-2026 Ronan LE MEILLAT
 * License: AGPL-3.0-or-later
 *
 * Generic CMS page renderer — displays static multilingual content
 * for informational pages referenced from the footer (about, legal, help, etc.).
 */

import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";

import DefaultLayout from "@/layouts/default";
import { getCmsPage } from "@/config/cms-content";
import { title as titleCls } from "@/shared/ui/primitives";
import { PageNotFound } from "@/pages/404";
import { getSiteContent } from "@/lib/store-api";

export default function CmsPage() {
  const { handle } = useParams<{ handle: string }>();
  const { i18n } = useTranslation();
  const { data: siteContent = [] } = useQuery({ queryKey: ["site-content"], queryFn: getSiteContent });

  const aliases: Record<string, string> = { returns: "returns-policy", privacy: "privacy-policy", terms: "terms-of-service", cookies: "cookie-policy", shipping: "shipping-policy" };
  const resolvedHandle = aliases[handle ?? ""] || handle || "";
  const page = getCmsPage(resolvedHandle, i18n.language);
  const managed = siteContent.find((item) => item.key === "footer-" + resolvedHandle || item.key === "footer-" + handle);

  if (!page) {
    return <PageNotFound />;
  }

  const isRtl = ["ar", "he"].some((lang) =>
    i18n.language.startsWith(lang),
  );

  return (
    <DefaultLayout>
      <section
        className="flex flex-col items-center justify-start gap-6 py-10 md:py-16"
        dir={isRtl ? "rtl" : "ltr"}
      >
        <div className="w-full max-w-3xl px-4">
          {/* Page title */}
          <h1 className={titleCls({ class: "mb-4" })}>{managed?.title || page.title}</h1>

          {/* Lead paragraph */}
          {managed?.body ? <div className="prose dark:prose-invert max-w-none mb-10" dangerouslySetInnerHTML={{ __html: managed.body }} /> : <p className="text-default-600 text-lg leading-relaxed mb-10">{page.lead}</p>}

          {/* Sections */}
          {!managed?.body && <div className="flex flex-col gap-8">
            {page.sections.map((section, idx) => (
              <article key={idx}>
                <h2 className="text-xl font-semibold text-foreground mb-2">
                  {section.heading}
                </h2>
                <p className="text-default-600 leading-relaxed">{section.body}</p>
              </article>
            ))}
          </div>}
        </div>
      </section>
    </DefaultLayout>
  );
}
